export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: Date;
  caller?: string;
}

class Logger {
  private minLevel: LogLevel;
  private isDevelopment: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.minLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.INFO;

    console.info(`[Logger] Environment: ${this.isDevelopment ? 'development' : 'production'}, min level: ${LogLevel[this.minLevel]}`);
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.minLevel;
  }

  private formatMessage(level: LogLevel, message: string, data?: any): void {
    if (!this.shouldLog(level)) return;

    const timestamp = new Date().toISOString();
    const levelName = LogLevel[level];
    const caller = this.getCaller();
    
    if (this.isDevelopment) {
      // Enhanced console logging for development
      const style = this.getConsoleStyle(level);
      console.groupCollapsed(`%c[${levelName}] ${caller}`, style, message);
      console.log(`⏰ ${timestamp}`);
      if (data !== undefined) {
        console.log('📊 Data:', data);
      }
      console.groupEnd();
    } else {
      // Production logging - store critical errors in localStorage
      if (level >= LogLevel.ERROR) {
        this.storeInLocalStorage({ level, message, data, timestamp: new Date(), caller });
      }
    }
  }

  private getCaller(): string {
    try {
      const stack = new Error().stack;
      if (!stack) return 'unknown';
      
      const lines = stack.split('\n');
      // Skip Error, getCaller, formatMessage, and the actual log method (debug/info/warn/error)
      for (let i = 4; i < lines.length; i++) {
        const line = lines[i];
        if (line && !line.includes('logger.ts')) {
          // Extract filename and line number
          const match = line.match(/at\s+(.+)\s+\((.+):(\d+):(\d+)\)/);
          if (match) {
            const [, functionName, filePath, lineNum] = match;
            const fileName = filePath.split('/').pop() || filePath;
            return `${fileName}:${lineNum} (${functionName})`;
          }
          
          // Fallback for different stack formats
          const simpleMatch = line.match(/at\s+(.+):(\d+):(\d+)/);
          if (simpleMatch) {
            const [, filePath, lineNum] = simpleMatch;
            const fileName = filePath.split('/').pop() || filePath;
            return `${fileName}:${lineNum}`;
          }
        }
      }
      
      return 'unknown';
    } catch {
      return 'unknown';
    }
  }

  private getConsoleStyle(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG:
        return 'color: #6B7280; font-weight: normal;';
      case LogLevel.INFO:
        return 'color: #3B82F6; font-weight: bold;';
      case LogLevel.WARN:
        return 'color: #F59E0B; font-weight: bold;';
      case LogLevel.ERROR:
        return 'color: #EF4444; font-weight: bold;';
      default:
        return 'color: #000000; font-weight: normal;';
    }
  }

  private storeInLocalStorage(entry: LogEntry): void {
    try {
      const key = 'app_error_logs';
      const existingLogs = JSON.parse(localStorage.getItem(key) || '[]');
      existingLogs.push(entry);
      
      // Keep only last 50 error logs
      if (existingLogs.length > 50) {
        existingLogs.splice(0, existingLogs.length - 50);
      }
      
      localStorage.setItem(key, JSON.stringify(existingLogs));
    } catch (error) {
      // Fallback to console if localStorage fails
      console.error('Failed to store log in localStorage:', error);
    }
  }

  debug(message: string, data?: any): void {
    this.formatMessage(LogLevel.DEBUG, message, data);
  }

  info(message: string, data?: any): void {
    this.formatMessage(LogLevel.INFO, message, data);
  }

  warn(message: string, data?: any): void {
    this.formatMessage(LogLevel.WARN, message, data);
  }

  error(message: string, data?: any): void {
    this.formatMessage(LogLevel.ERROR, message, data);
  }

  // Method to retrieve stored error logs (useful for debugging)
  getStoredLogs(): LogEntry[] {
    try {
      return JSON.parse(localStorage.getItem('app_error_logs') || '[]');
    } catch {
      return [];
    }
  }

  // Method to clear stored logs
  clearStoredLogs(): void {
    try {
      localStorage.removeItem('app_error_logs');
    } catch (error) {
      console.error('Failed to clear stored logs:', error);
    }
  }
}

export const logger = new Logger();
